import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import {
  parseWireDate,
  formatWireDate,
  resolvePreset,
  startOfDay,
} from '@/entities/legacy-date-range';
import { LegacyClientsPage } from '@/pages/legacy-clients';
import { requireAuth } from '../require-auth';

/**
 * URL state for the legacy Клиенты screen — a 1-to-1 port of the old panel's
 * `/clients` page. The URL is the single source of truth: `search` (free
 * text over the client name) and `from`/`to` (the week range) scope the list.
 *
 * There is no new-design counterpart: this route always renders the legacy
 * page, in both modes. It is reached from the legacy dashboard's "New
 * Clients" tile.
 */
const deliveryClientsSearchSchema = z.object({
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /**
   * 1-based page index. Defaulted (not just `.catch()`ed) so navigating here
   * from the dashboard tile doesn't have to supply it.
   */
  page: z.number().int().min(1).default(1).catch(1),
  /** Rows per page. Defaulted for the same reason as `page`. */
  limit: z.number().int().min(1).default(15).catch(15),
});

export const Route = createFileRoute('/delivery/clients')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: deliveryClientsSearchSchema,
  component: DeliveryClientsRoute,
});

/**
 * The legacy window from `from`/`to`, defaulting to the current WEEK when
 * either is absent or malformed — the old page queried
 * `startOf("week")..today` with no params. A one-sided range is treated as no
 * range at all rather than half-applied.
 */
function readLegacyRange(from: string | undefined, to: string | undefined) {
  const parsedFrom = parseWireDate(from);
  const parsedTo = parseWireDate(to);
  if (!parsedFrom || !parsedTo) return resolvePreset('week');
  // Tolerate a reversed range from a hand-edited URL.
  return parsedFrom <= parsedTo
    ? { from: parsedFrom, to: parsedTo }
    : { from: parsedTo, to: parsedFrom };
}

function DeliveryClientsRoute() {
  const { search, page, limit, from, to } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const range = readLegacyRange(from, to);

  return (
    <LegacyClientsPage
      search={search ?? ''}
      range={range}
      page={page}
      limit={limit}
      onRangeChange={(next) => {
        void navigate({
          search: (prev) => ({
            ...prev,
            from: formatWireDate(startOfDay(next.from)),
            to: formatWireDate(startOfDay(next.to)),
          }),
        });
      }}
      onSearchChange={(next) => {
        void navigate({
          search: (prev) => ({ ...prev, search: next || undefined }),
        });
      }}
      onPageChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, page: next }) });
      }}
      onLimitChange={(next) => {
        // A new page size restarts paging — page 4 of 15-per-page points
        // nowhere once the pages are 55 rows tall.
        void navigate({ search: (prev) => ({ ...prev, limit: next, page: 1 }) });
      }}
    />
  );
}
